import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Smartphone } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { installOption, manualKindFor, STEP_KEYS } from '@/lib/installGuide';
import { isStandalone, markInstalled, promptInstall, subscribeInstall } from '@/lib/pwaInstall';

/**
 * « Installer Ridalo » — one entry of the existing menu (Lot PWA 5).
 *
 * Nothing happens by itself: no popup, no banner, no install dialog and no notification permission are ever
 * triggered on load. The entry is only a button; the browser's native install dialog (Android) or a small
 * explanatory panel (iPhone, Android without the native dialog, in-app browsers) opens ONLY when the person
 * taps it. The entry disappears when Ridalo already runs as an installed app, and on desktop browsers.
 * The notification setup is left to the existing « Notifications sur cet appareil » page.
 */
/**
 * The install logic shared by every « Installer Ridalo » button (menu entry, provider dashboard card): what the
 * device can do (`option`), the tap handler (native dialog, or the instructions panel) and the panel state.
 * Nothing runs by itself: the handler is only called from a tap.
 */
export function useInstallFlow() {
  const { t } = useTranslation();
  const [, tick] = useState(0);
  const [panel, setPanel] = useState(null);          // null | { kind } | { declined: true }
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribeInstall(() => tick((n) => n + 1)), []);
  useEffect(() => { if (isStandalone()) markInstalled(); }, []);   // an installed app remembers it, so a browser tab does not offer it again

  const option = installOption();

  const onClick = async () => {
    if (busy) return;
    if (option?.kind === 'native') {
      setBusy(true);
      try {
        const outcome = await promptInstall();             // the person tapped: the native dialog may open now
        if (outcome === 'accepted') toast.success(t('pwa.menu.accepted'));
        else if (outcome === 'dismissed') setPanel({ declined: true });      // refusal respected, no insistence
        else setPanel({ kind: manualKindFor() });                            // the browser no longer offers the dialog
      } finally { setBusy(false); }
      return;
    }
    if (option) setPanel({ kind: option.kind });
  };

  return { option, busy, onClick, panel, setPanel };
}

/** The explanatory panel (steps per browser) opened by `useInstallFlow().onClick`. */
export function InstallGuideDialog({ panel, setPanel }) {
  const { t, i18n } = useTranslation();
  const base = `pwa.menu.kinds.${panel?.kind}`;
  const steps = panel?.kind ? STEP_KEYS.filter((k) => i18n.exists(`${base}.${k}`)) : [];
  return (
    <Dialog open={Boolean(panel)} onOpenChange={(open) => { if (!open) setPanel(null); }}>
      <DialogContent className="max-w-md" data-testid="install-panel" data-kind={panel?.declined ? 'declined' : panel?.kind}>
        {panel && (
          <>
            <DialogHeader>
              <DialogTitle>{panel.declined ? t('pwa.menu.declined.title') : t(`${base}.title`)}</DialogTitle>
              <DialogDescription className="sr-only">{t('pwa.menu.entry')}</DialogDescription>
            </DialogHeader>
            {panel.declined ? (
              <div className="space-y-2 text-sm text-ink-light">
                <p>{t('pwa.menu.declined.body')}</p>
                <p>{t('pwa.menu.declined.note')}</p>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-ink-light">
                {i18n.exists(`${base}.body`) && <p data-testid="install-panel-body">{t(`${base}.body`)}</p>}
                {steps.length > 0 && (
                  <ol className="list-decimal pl-5 space-y-2" data-testid="install-panel-steps">
                    {steps.map((k) => <li key={k}>{t(`${base}.${k}`)}</li>)}
                  </ol>
                )}
                {i18n.exists(`${base}.note`) && <p className="text-xs">{t(`${base}.note`)}</p>}
                {steps.length > 0 && <p className="text-xs" data-testid="install-panel-after">{t('pwa.menu.after')}</p>}
              </div>
            )}
            <button type="button" onClick={() => setPanel(null)} className="mt-2 w-full rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white" data-testid="install-panel-close">
              {t('pwa.menu.close')}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function InstallMenuEntry({ variant = 'mobile' }) {
  const { t } = useTranslation();
  const { option, busy, onClick, panel, setPanel } = useInstallFlow();
  if (!option && !panel) return null;
  const className = variant === 'desktop'
    ? 'inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-accent transition-colors'
    : 'flex items-center gap-2 text-base font-medium text-primary text-left';

  return (
    <>
      {option && (
        <button type="button" onClick={onClick} disabled={busy} className={className} data-testid={variant === 'desktop' ? 'install-entry' : 'mobile-install-entry'} data-kind={option.kind}>
          <Smartphone className="w-4 h-4" aria-hidden="true" />
          {t('pwa.menu.entry')}
        </button>
      )}
      <InstallGuideDialog panel={panel} setPanel={setPanel} />
    </>
  );
}
